import React from 'react';
import { motion } from 'framer-motion';
import CustomChartRenderer from './CustomChartRenderer';
import {
  LineChart,
  BarChart,
  AreaChart,
  Line,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Metric } from '../types';

interface FullscreenChartModalProps {
  metric: Metric;
  onClose: () => void;
}

const FullscreenChartModal: React.FC<FullscreenChartModalProps> = ({ metric, onClose }) => {
  // If it's a custom chart, render the custom chart component
  if (metric.chart_type === 'custom') {
    return (
      <motion.div
        className="fixed inset-0 bg-black/90 backdrop-blur-lg z-50 flex flex-col"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <div 
          className="relative w-full h-full flex flex-col p-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-light">{metric.metric_name}</h2>
            <button
              onClick={onClose}
              className="text-2xl text-light hover:text-accent-blue transition-colors"
            >
              &times;
            </button>
          </div>
          
          <div className="flex-grow bg-dark-navy rounded-xl p-4 overflow-y-auto">
            <CustomChartRenderer metric={metric} />
          </div>
        </div>
      </motion.div>
    );
  }

  // Convert metric data to recharts compatible format
  const chartData = metric.data
    .filter(item => {
      // Filter out invalid entries where both x and y values are empty
      return !(item.x_value === '' && (item.y_value === '' || item.y_value === null || item.y_value === undefined));
    })
    .map(item => {
      // Process X value - keep as string for categorical data or convert if numeric
      const x = typeof item.x_value === 'number' ? item.x_value : String(item.x_value);
      
      // Process Y value - ensure it's numeric for proper line chart rendering
      let y: number;
      if (typeof item.y_value === 'number') {
        y = item.y_value;
      } else {
        const parsedValue = parseFloat(item.y_value.toString());
        y = isNaN(parsedValue) ? 0 : parsedValue;
      }
      
      return { x, y };
    });

  const renderChart = () => {
    switch (metric.chart_type) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height="80%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
              <XAxis 
                dataKey="x" 
                stroke="#94a3b8" 
                label={{ value: metric.x_label, position: 'insideBottom', offset: -10 }} 
              />
              <YAxis 
                stroke="#94a3b8" 
                label={{ value: metric.y_label, angle: -90, position: 'insideLeft', offset: -15 }} 
              />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#e2e8f0' }} 
                itemStyle={{ color: '#e2e8f0' }}
              />
              <Legend formatter={() => ''} />
              <Line 
                type="monotone" 
                dataKey="y" 
                stroke="#3a86ff" 
                activeDot={{ r: 8 }} 
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height="80%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
              <XAxis 
                dataKey="x" 
                stroke="#94a3b8" 
                label={{ value: metric.x_label, position: 'insideBottom', offset: -10 }} 
              />
              <YAxis 
                stroke="#94a3b8" 
                label={{ value: metric.y_label, angle: -90, position: 'insideLeft', offset: -15 }} 
              />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#e2e8f0' }} 
                itemStyle={{ color: '#e2e8f0' }}
              />
              <Legend formatter={() => ''} />
              <Bar dataKey="y" fill="#3a86ff" />
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'area':
        return (
          <ResponsiveContainer width="100%" height="80%">
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
              <XAxis 
                dataKey="x" 
                stroke="#94a3b8" 
                label={{ value: metric.x_label, position: 'insideBottom', offset: -10 }} 
              />
              <YAxis 
                stroke="#94a3b8" 
                label={{ value: metric.y_label, angle: -90, position: 'insideLeft', offset: -15 }} 
              />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#e2e8f0' }} 
                itemStyle={{ color: '#e2e8f0' }}
              />
              <Legend formatter={() => ''} />
              <Area 
                type="monotone" 
                dataKey="y" 
                stackId="1" 
                stroke="#3a86ff" 
                fill="#3a86ff" 
                fillOpacity={0.6} 
              />
            </AreaChart>
          </ResponsiveContainer>
        );
      
      default:
        return (
          <div className="text-center py-10 text-medium-gray">
            Unsupported chart type: {metric.chart_type}
          </div>
        );
    }
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black/90 backdrop-blur-lg z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <div 
        className="bg-dark-navy rounded-2xl w-full max-w-4xl max-h-[90vh] p-6 relative overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-2xl text-light hover:text-accent-blue transition-colors"
        >
          &times;
        </button>
        
        <h2 className="text-2xl font-bold text-light mb-6">{metric.metric_name}</h2>
        
        <div className="h-[60vh]">
          {renderChart()}
        </div>
      </div>
    </motion.div>
  );
};

export default FullscreenChartModal;